import { Box, Typography } from '@mui/material'
import { TimelineChart } from './TimelineChart'
import { BASELINE_COLOR, type Group, type SeriesPoint } from './dataPipeline'
import type { Facet } from '~/state/useChartKnobsState'
import { MESSAGES } from './chrome/chartChrome'

interface Props {
  hasData: boolean
  points: SeriesPoint[]
  groups: Group[]
  facet: Facet
  inactiveSeries: string[]
  yDomain: [number, number]
  /** T5 fix round 1: FY-aware tick formatter for the baseline segment, built by ChartCanvas
   *  against the baseline's own extended lower bound (never queryFromIso — see ChartCanvas's
   *  Props doc on baselineFromIso). Undefined outside 'fy' / non-FY callers, so TimelineChart
   *  falls back to its own default shortDateTick, unchanged. */
  tickFormatter?: (v: string | number) => string
}

/** Row 15-light previous-period baseline slot (extracted from ChartCanvas, FU-g rider). Renders
 *  the grayed baseline panel, or the LOUD no-data hint — never silently omits the requested
 *  baseline. Wave-3 §2 (D3): the Σ/ø/Δ chip moved OUT to the panel-aligned PanelStatRow (cell 0,
 *  built by ChartCanvas) — this component owns only the chart slot itself now. */
export function BaselinePanel({
  hasData,
  points,
  groups,
  facet,
  inactiveSeries,
  yDomain,
  tickFormatter,
}: Props) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }} data-baseline-panel="true">
      {hasData ? (
        <TimelineChart
          points={points}
          groups={groups.map((g) => ({ ...g, color: BASELINE_COLOR }))}
          facet={facet}
          inactiveSeries={inactiveSeries}
          yDomain={yDomain}
          hideYAxis={false}
          tickFormatter={tickFormatter}
        />
      ) : (
        // No pre-window data: keep the slot LOUD (no-silent-fallbacks) — never
        // silently omit the requested baseline.
        <Typography variant="caption" color="text.secondary">
          {MESSAGES.noDataInPreviousPeriod}
        </Typography>
      )}
    </Box>
  )
}
